import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../static/css/App.css";
import "../../static/css/customerTable.css";
import "bootstrap/dist/css/bootstrap.min.css";

import { Container, Button, Modal, Form, Row, Col } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import moment from "moment";
import { showConfirm } from "react-confirm-prompt";

import DynamicBreadcrumb from "../header/breadcrumb";
import Loader from "../loader/loader";
import SearchableDropdown from "../components/SearchableDropdown";

import branchApi from "../../services/branchapi";
import deliveryCancelReasonsApi from "../../services/deliveryCancelReasonsApi";

const DeliveryAppCancelReasonsList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);

  const [refreshTable, setRefreshTable] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });

  // filters
  const [status, setStatus] = useState("ALL"); // ALL | 1 | 0
  const [reasonType, setReasonType] = useState("DE_EXEC_CANCEL"); // default for delivery exec app

  const [branchId, setBranchId] = useState(0);
  const [branchName, setBranchName] = useState("All (Global + Branch)");
  const [allBranches, setAllBranches] = useState([]);

  // modal add/edit
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [form, setForm] = useState({
    reason_type: "DE_EXEC_CANCEL",
    reason_text: "",
    is_note_required: 0,
    branch_id: null,
    sort_order: 1,
    status: 1,
  });

  const [errors, setErrors] = useState({});
  const [loaderVisible, setLoaderVisible] = useState(false);

  const pathArray = [{ name: "Delivery App Cancel Reasons", url: null }];

  const resetForm = () => {
    setForm({
      reason_type: reasonType || "DE_EXEC_CANCEL",
      reason_text: "",
      is_note_required: 0,
      branch_id: null,
      sort_order: 1,
      status: 1,
    });
    setErrors({});
  };

  const openAddModal = () => {
    setEditingRow(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setForm({
      reason_type: row.reason_type,
      reason_text: row.reason_text || "",
      is_note_required: Number(row.is_note_required || 0),
      branch_id: row.branch_id || null,
      sort_order: Number(row.sort_order || 1),
      status: Number(row.status ?? 1),
    });
    setErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const e = {};
    if (!form.reason_type) e.reason_type = "Reason type is required";
    if (!form.reason_text || !form.reason_text.trim()) e.reason_text = "Reason text is required";
    if (!form.sort_order || Number(form.sort_order) <= 0) e.sort_order = "Sort order must be > 0";
    return e;
  };

  const deriveSearch = () => {
    const q = filterModel?.quickFilterValues?.[0];
    return q ? String(q) : null;
  };

  const mapStatusToApi = () => (status === "ALL" ? null : status);

  // branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const resp = await branchApi.getAllBranches({ status: 1, limit: 500, page: 0 });
        const list = (resp?.data?.result || []).sort((a, b) =>
          (a.branchName || "").localeCompare(b.branchName || "")
        );
        setAllBranches(list);
      } catch (err) {
        console.error("Failed to fetch branches:", err);
      }
    };
    fetchBranches();
  }, []);

  const fetchReasons = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;

      const resp = await deliveryCancelReasonsApi.list({
        limit: pageSize,
        page,
        search: deriveSearch(),
        reason_type: reasonType || null,
        status: mapStatusToApi(),
        branch_id: branchId && Number(branchId) > 0 ? Number(branchId) : null,
      });

      const result = resp?.data?.result || [];
      const total = resp?.data?.totalRecords?.[0]?.total_records || 0;

      const formatted = result.map((r) => ({
        id: r.id,
        reason_type: r.reason_type,
        reason_text: r.reason_text,
        is_note_required: Number(r.is_note_required || 0),
        branch_id: r.branch_id,
        branch_name: r.branch_id
          ? (allBranches.find((b) => Number(b.branchId) === Number(r.branch_id))?.branchName || `Branch #${r.branch_id}`)
          : "GLOBAL",
        sort_order: r.sort_order,
        status: r.status,
        created_at: r.created_at ? moment(r.created_at).format("YYYY/MM/DD kk:mm:ss") : "",
        updated_at: r.updated_at ? moment(r.updated_at).format("YYYY/MM/DD kk:mm:ss") : "",
      }));

      setRows(formatted);
      setTotalRecords(total);
    } catch (err) {
      console.error("Error fetching cancel reasons:", err);
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, refreshTable, status, reasonType, branchId, allBranches]);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  const handleSave = async (e) => {
    e.preventDefault();

    const eMap = validateForm();
    if (Object.keys(eMap).length > 0) {
      setErrors(eMap);
      return;
    }
    setErrors({});

    try {
      setLoaderVisible(true);

      const payload = {
        reason_type: form.reason_type,
        reason_text: form.reason_text.trim(),
        is_note_required: Number(form.is_note_required || 0),
        branch_id: form.branch_id ? Number(form.branch_id) : null,
        sort_order: Number(form.sort_order || 1),
        status: Number(form.status ?? 1),
      };

      let resp;
      if (editingRow?.id) resp = await deliveryCancelReasonsApi.update(editingRow.id, payload);
      else resp = await deliveryCancelReasonsApi.create(payload);

      // ✅ close modal first, then show confirm (z-index fix)
      setShowModal(false);

      setTimeout(() => {
        if (resp?.status === "SUCCESS" || resp?.success === true) {
          showConfirm("Success", {
            description: resp?.message || "Saved successfully",
            type: "Success",
            hideCancel: true,
          }).then(() => setRefreshTable((p) => !p));
        } else {
          showConfirm("Oops!", {
            description: resp?.message || "Process failed, try again.",
            type: "warning",
            hideCancel: true,
          });
        }
      }, 200);
    } catch (err) {
      console.error(err);
      setShowModal(false);
      setTimeout(() => {
        showConfirm("Oops!", {
          description: err?.message || "Process failed, try again.",
          type: "warning",
          hideCancel: true,
        });
      }, 200);
    } finally {
      setLoaderVisible(false);
    }
  };

  const handleToggleStatus = async (row) => {
    const isActive = Number(row.status) === 1;
    const nextStatus = isActive ? 0 : 1;

    showConfirm(isActive ? "Change status to inactive?" : "Change status to active?", {
      description: isActive ? "This will inactivate the reason" : "This will activate the reason",
      type: "info",
    }).then(async (answer) => {
      if (!answer) return;

      try {
        setLoaderVisible(true);
        const resp = await deliveryCancelReasonsApi.updateStatus(row.id, nextStatus);

        setTimeout(() => {
          if (resp?.status === "SUCCESS" || resp?.success === true) {
            showConfirm("Success", {
              description: resp?.message || "Status updated",
              type: "Success",
              hideCancel: true,
            }).then(() => setRefreshTable((p) => !p));
          } else {
            showConfirm("Oops!", {
              description: resp?.message || "Unable to update status",
              type: "warning",
              hideCancel: true,
            });
          }
        }, 50);
      } catch (err) {
        console.error(err);
        showConfirm("Oops!", {
          description: err?.message || "Unable to update status",
          type: "warning",
          hideCancel: true,
        });
      } finally {
        setLoaderVisible(false);
      }
    });
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "reason_type", headerName: "Type", width: 160 },
      { field: "branch_name", headerName: "Scope", width: 200 },
      { field: "reason_text", headerName: "Reason", flex: 1, minWidth: 300 },
      {
        field: "is_note_required",
        headerName: "Note Req",
        width: 110,
        renderCell: (p) => (Number(p.value) === 1 ? "Yes" : "No"),
      },
      { field: "sort_order", headerName: "Sort", width: 90 },
      {
        field: "status",
        headerName: "Status",
        width: 110,
        renderCell: (p) => (Number(p.value) === 1 ? "Active" : "Inactive"),
      },
      { field: "updated_at", headerName: "Updated At", width: 190 },
      {
        field: "actions",
        headerName: "Actions",
        width: 220,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const isActive = Number(row.status) === 1;
          return (
            <div style={{ display: "flex", gap: 8 }}>
              <Button size="sm" variant="primary" onClick={() => openEditModal(row)}>
                Edit
              </Button>
              <Button
                size="sm"
                variant={isActive ? "warning" : "success"}
                onClick={() => handleToggleStatus(row)}
              >
                {isActive ? "Inactivate" : "Activate"}
              </Button>
            </div>
          );
        },
      },
    ],
    [allBranches]
  );

  return (
    <Container fluid>
      <DynamicBreadcrumb path={pathArray} />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 style={{ margin: 0 }}>Delivery App Cancel Reasons</h5>
        <Button variant="success" onClick={openAddModal}>
          + Add Reason
        </Button>
      </div>

      {/* Filters */}
      <div className="card p-2 mb-2">
        <Row className="g-2">
          <Col md={4}>
            <Form.Label className="mb-1">Reason Type</Form.Label>
            <Form.Select
              value={reasonType}
              onChange={(e) => {
                setReasonType(e.target.value);
                setRefreshTable((p) => !p);
              }}
            >
              <option value="DE_EXEC_CANCEL">Delivery Executive Cancel</option>
              <option value="CUSTOMER_CANCEL">Customer Cancel</option>
              <option value="BRANCH_CANCEL">Branch Cancel</option>
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label className="mb-1">Status</Form.Label>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="ALL">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Form.Select>
          </Col>

          <Col md={5}>
            <Form.Label className="mb-1">Branch (optional)</Form.Label>
            <SearchableDropdown
              options={allBranches}
              extraoption={{ branchId: 0, branchName: "All (Global + Branch)" }}
              label="branchName"
              id="branchId"
              selectedVal={branchName}
              handleChange={(v) => {
                setBranchId(v?.id ?? 0);
                setBranchName(v?.name ?? "All (Global + Branch)");
              }}
            />
          </Col>
        </Row>

        <div className="mt-2" style={{ fontSize: 12, opacity: 0.8 }}>
          Tip: Use the table search (top right) to search by reason text.
        </div>
      </div>

      {/* Table */}
      <Box
        sx={{
          height: 650,
          width: "100%",
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
          [`& .${gridClasses.row}`]: { borderBottom: "1px solid #f0f0f0" },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          sortingMode="server"
          filterMode="server"
          paginationMode="server"
          rowCount={totalRecords}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pageSizeOptions={[10, 15, 25, 50]}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } },
          }}
        />
      </Box>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingRow ? "Edit Reason" : "Add Reason"}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>Reason Type</Form.Label>
                <Form.Select
                  value={form.reason_type}
                  onChange={(e) => setForm((p) => ({ ...p, reason_type: e.target.value }))}
                  isInvalid={!!errors.reason_type}
                >
                  <option value="DE_EXEC_CANCEL">DE_EXEC_CANCEL</option>
                  <option value="CUSTOMER_CANCEL">CUSTOMER_CANCEL</option>
                  <option value="BRANCH_CANCEL">BRANCH_CANCEL</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.reason_type}</Form.Control.Feedback>
              </Col>

              <Col md={8}>
                <Form.Label>Branch (optional, leave empty = GLOBAL)</Form.Label>
                <SearchableDropdown
                  options={allBranches}
                  extraoption={{ branchId: 0, branchName: "GLOBAL (No Branch)" }}
                  label="branchName"
                  id="branchId"
                  selectedVal={
                    form.branch_id
                      ? (allBranches.find((b) => Number(b.branchId) === Number(form.branch_id))?.branchName || "")
                      : "GLOBAL (No Branch)"
                  }
                  handleChange={(v) => setForm((p) => ({ ...p, branch_id: v?.id ? Number(v.id) : null }))}
                />
              </Col>

              <Col md={12}>
                <Form.Label>Reason Text</Form.Label>
                <Form.Control
                  type="text"
                  value={form.reason_text}
                  onChange={(e) => setForm((p) => ({ ...p, reason_text: e.target.value }))}
                  isInvalid={!!errors.reason_text}
                  placeholder="e.g. Customer not reachable"
                />
                <Form.Control.Feedback type="invalid">{errors.reason_text}</Form.Control.Feedback>
              </Col>

              <Col md={4}>
                <Form.Label>Note Required</Form.Label>
                <Form.Select
                  value={form.is_note_required}
                  onChange={(e) => setForm((p) => ({ ...p, is_note_required: Number(e.target.value) }))}
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                  isInvalid={!!errors.sort_order}
                />
                <Form.Control.Feedback type="invalid">{errors.sort_order}</Form.Control.Feedback>
              </Col>

              <Col md={4}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </Form.Select>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingRow ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {loaderVisible ? <Loader /> : null}
    </Container>
  );
};

export default DeliveryAppCancelReasonsList;